<template>
  <v-select
    v-model="selectedDate"
    :items="rateChangeLabels"
    :label="$t('rateChangeDate')"
    item-value="effectiveDate"
    return-object
    outlined
    style="width: fit-content;"
    @change="onDateSelected"
  >
    <template #selection="{ item }">
      <span class="text-no-wrap">
        <v-tooltip bottom color="primary">
          <template #activator="{ on }"><span v-on="on">{{ item.startFormatted }}</span></template>
          <span class="subtitle-1 white--text">{{ item.startTooltip }}</span>
          <div v-if="item.note" class="subtitle-2 white--text" style="max-width: 300px;">{{ item.note }}</div>
        </v-tooltip>
        <span> - </span>
        <v-tooltip v-if="item.endTooltip" bottom color="primary">
          <template #activator="{ on }"><span v-on="on">{{ item.endFormatted }}</span></template>
          <span class="subtitle-1 white--text">{{ item.endTooltip }}</span>
        </v-tooltip>
        <span v-else>{{ item.endFormatted }}</span>
      </span>
    </template>
    <template #item="{ item }">
      <span class="text-no-wrap">
        <v-tooltip bottom color="primary">
          <template #activator="{ on }"><span v-on="on">{{ item.startFormatted }}</span></template>
          <span class="subtitle-1 white--text">{{ item.startTooltip }}</span>
          <div v-if="item.note" class="subtitle-2 white--text" style="max-width: 300px;">{{ item.note }}</div>
        </v-tooltip>
        <span> - </span>
        <v-tooltip v-if="item.endTooltip" bottom color="primary">
          <template #activator="{ on }"><span v-on="on">{{ item.endFormatted }}</span></template>
          <span class="subtitle-1 white--text">{{ item.endTooltip }}</span>
        </v-tooltip>
        <span v-else>{{ item.endFormatted }}</span>
      </span>
    </template>
    <template #append-outer>
      <slot name="append-outer" :selectedDate="selectedDate" />
    </template>
  </v-select>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ScheduledRateChangeStatus } from '@/utils/Enumerations.js'
import { utcToCompanyOffsetDstSensitive } from '@/utils/DateFormatter.js'
import timezones from '@/components/settings-components/company-config/timezones.json'

export default {
  name: 'RateChangeDateSelect',

  props: {
    scheduledRateChanges: {
      type: Array,
      required: true
    },
    contract: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    selectedDate: { effectiveDate: null },
    datedActivities: [],
    initialized: false
  }),

  computed: {
    ...mapGetters('activity', ['allActivities', 'activitiesByEntity']),
    ...mapGetters('user', ['businessEntities', 'companyInfo']),

    tzAbbr () {
      const tz = timezones.find(tz => Number(tz.offset) === this.companyInfo.timeZoneOffset)
      return tz?.name?.split('(')[1]?.split(')')[0] ?? ''
    },

    rateChangeLabels () {
      if (!this.contract?.effectiveDate) return []

      const appliedChanges = (Array.isArray(this.scheduledRateChanges) ? this.scheduledRateChanges : [])
        .filter(rc => rc.status === ScheduledRateChangeStatus.Applied.value)
        .sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate))

      const formatTooltip = (dateStr) => {
        const local = utcToCompanyOffsetDstSensitive(dateStr)
        if (local.hours() === 0 && local.minutes() === 0) return this.tzAbbr ? `${this.$t('midnight')} ${this.tzAbbr}` : this.$t('midnight')
        const timeStr = local.format('h:mm A')
        return this.tzAbbr ? `${timeStr} ${this.tzAbbr}` : timeStr
      }

      const buildLabel = (startDate, endDate, note = null) => {
        const startFormatted = utcToCompanyOffsetDstSensitive(startDate).format('MM/DD/YYYY')
        const endFormatted = utcToCompanyOffsetDstSensitive(endDate).format('MM/DD/YYYY')
        return {
          effectiveDate: startDate,
          startFormatted,
          startTooltip: formatTooltip(startDate),
          endFormatted,
          endTooltip: formatTooltip(endDate),
          note
        }
      }

      const currentRangeStart = appliedChanges.length > 0
        ? appliedChanges[appliedChanges.length - 1].effectiveDate
        : this.contract.effectiveDate
      const currentStartFormatted = utcToCompanyOffsetDstSensitive(currentRangeStart).format('MM/DD/YYYY')

      const labels = [{
        effectiveDate: this.$t('current'),
        startFormatted: currentStartFormatted,
        startTooltip: formatTooltip(currentRangeStart),
        endFormatted: this.$t('current'),
        endTooltip: null,
        note: appliedChanges[appliedChanges.length - 1]?.note ?? null
      }]

      const intermediatePeriods = appliedChanges.slice(0, -1).map((rc, index) =>
        buildLabel(rc.effectiveDate, appliedChanges[index + 1].effectiveDate, rc.note)
      )

      const rangeLabels = appliedChanges.length > 0
        ? [buildLabel(this.contract.effectiveDate, appliedChanges[0].effectiveDate), ...intermediatePeriods]
        : []

      return labels.concat(rangeLabels.reverse())
    },

    allActivitiesToDisplay () {
      return this.selectedDate?.effectiveDate === this.$t('current') ? this.allActivities : this.datedActivities
    },

    entitiesFromActivities () {
      const entitiesMap = new Map()
      this.datedActivities.forEach(activity => {
        const entityId = activity.activityTemplate.businessEntityId
        const businessEntity = this.businessEntities.find(be => be.businessEntityId === entityId)
        if (!entitiesMap.has(entityId)) {
          entitiesMap.set(entityId, {
            businessEntityId: entityId,
            name: businessEntity?.name ?? ''
          })
        }
      })
      return Array.from(entitiesMap.values())
    },

    entities () {
      return this.selectedDate?.effectiveDate === this.$t('current') ? this.activitiesByEntity : this.entitiesFromActivities
    }
  },

  watch: {
    scheduledRateChanges (val) {
      if (!Array.isArray(val)) return
      if (!this.initialized) {
        this.initialize()
      }
    }
  },

  methods: {
    ...mapActions('activity', ['fetchActivitiesAsOfDate']),

    initialize () {
      if (this.rateChangeLabels.length > 0) {
        this.selectedDate = this.rateChangeLabels[0]
        this.initialized = true
        this.emitChange()
      }
    },

    emitChange () {
      this.$emit('change', {
        activities: this.allActivitiesToDisplay,
        entities: this.entities,
        effectiveDate: this.selectedDate?.effectiveDate,
        period: this.buildPeriod(this.selectedDate)
      })
    },

    buildPeriod (selected) {
      if (!selected || selected.startFormatted == null) return null
      const timezone = this.tzAbbr
      // Tooltips embed the timezone (e.g. "8:00 AM CST"); strip it so the PDF can show the timezone once, in the label.
      const timezoneSuffix = timezone ? ` ${timezone}` : ''
      const stripTimezone = (tooltip) => (timezoneSuffix && tooltip && tooltip.endsWith(timezoneSuffix) ? tooltip.slice(0, -timezoneSuffix.length) : tooltip)
      const dateTime = (formatted, tooltip) => [formatted, stripTimezone(tooltip)].filter(Boolean).join(' ')
      // The open-ended "current" period has no end, so the PDF shows only the start date.
      const isOpenEnded = selected.effectiveDate === this.$t('current')
      // Compact YYYYMMDD form of the start date for use in the download filename (MM/DD/YYYY -> YYYYMMDD).
      const startDate = selected.startFormatted?.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3$1$2') ?? null
      return {
        start: dateTime(selected.startFormatted, selected.startTooltip),
        end: isOpenEnded ? null : dateTime(selected.endFormatted, selected.endTooltip),
        timezone,
        startDate
      }
    },

    async onDateSelected (src) {
      if (src.effectiveDate === this.$t('current')) {
        this.datedActivities = []
        this.emitChange()
        return
      }
      this.datedActivities = await this.fetchActivitiesAsOfDate({
        contractId: this.contract.contractId,
        asOfDate: src.effectiveDate
      })
      this.emitChange()
    }
  }
}
</script>
